import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, User, Mail, ShieldCheck, ShieldOff, Key, Coins, LogIn, ExternalLink,
  Users as UsersIcon, Building2, RefreshCw,
} from "lucide-react";
import { ig } from "../inbox/_inbox_shared";
import { api } from "../../../lib/apiClient";

const ROLE_OPTIONS = ["super_admin", "credit_admin", "support_admin", "agency_owner", "agent"];

export default function UserDetailPage() {
  const { userId } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null); const [err, setErr] = useState(null); const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);
  const [roles, setRoles] = useState([]);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const data = await api.get(`/api/admin/users/${userId}`);
      setD(data); setRoles(data.user.roles || []);
    } catch (e) { setErr(e.message); }
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const toggleRole = (r) => setRoles((cur) => cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r]);
  const saveRoles = async () => {
    setBusy(true); try { await api.post(`/api/admin/users/${userId}/role`, { roles }); setFlash("Roles updated"); await load(); }
    catch (e) { setFlash(e.message); } finally { setBusy(false); setTimeout(() => setFlash(null), 2500); }
  };
  const disable = async () => { if (!window.confirm("Disable this user's login?")) return; await api.post(`/api/admin/users/${userId}/disable`); setFlash("User disabled"); await load(); setTimeout(() => setFlash(null), 2500); };
  const enable  = async () => { await api.post(`/api/admin/users/${userId}/enable`);  setFlash("User enabled");  await load(); setTimeout(() => setFlash(null), 2500); };
  const reset   = async () => { if (!window.confirm("Reset access? A temporary password will be shown once.")) return; const r = await api.post(`/api/admin/users/${userId}/reset-password`); setFlash(`Temporary password: ${r.temporary_password}`); };
  const impersonate = async () => {
    if (!window.confirm("Impersonate this user? You will be logged in as them.")) return;
    const r = await api.post(`/api/admin/users/${userId}/impersonate`);
    api.setToken(r.token);
    setFlash(`Now impersonating ${r.email}. Refresh to continue as this user.`);
  };
  const adjustCredits = async (ws) => {
    const amountStr = window.prompt(`Adjust credits for ${ws.name}\n\nEnter delta (e.g. 500 or -100):`, "500");
    if (!amountStr) return;
    const amount = Number(amountStr);
    if (Number.isNaN(amount)) { alert("Not a number"); return; }
    const reason = window.prompt("Reason?", "manual_adjust") || "manual_adjust";
    const r = await api.post(`/api/admin/users/${userId}/credits-adjust`, { workspace_id: ws.id, amount, reason });
    setFlash(`Credits adjusted. New balance: ${r.balance}`); await load(); setTimeout(() => setFlash(null), 3000);
  };

  if (err)  return <div style={{ padding: 40, color: "#B91C1C" }}>Error: {err}</div>;
  if (!d)   return <div style={{ padding: 40, color: ig.muted }}>Loading…</div>;

  const u = d.user;
  return (
    <div data-testid="user-detail-page" style={{
      minHeight: "calc(100vh - 60px)", background: "#F6F8F7", padding: "20px 24px 60px",
      fontFamily: "Inter, sans-serif", color: ig.ink,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
        <button data-testid="back-to-contacts" onClick={() => nav(-1)} style={backBtn}><ArrowLeft size={13} /> Back</button>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{
            width: 44, height: 44, borderRadius: 999, background: "#EDE9FE", color: "#6D28D9",
            display: "inline-grid", placeItems: "center", fontSize: 15, fontWeight: 800,
          }}>{initials(u.name || u.email)}</span>
          <div>
            <h1 style={{ margin: 0, fontSize: 21, fontWeight: 800 }}>{u.name || u.email}</h1>
            <div style={{ fontSize: 12, color: ig.muted, marginTop: 2 }}>{u.email} · {u.id}</div>
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <StatusPill status={u.status || "active"} />
        <button onClick={load} style={btnGhost} data-testid="ud-refresh"><RefreshCw size={13} /> Refresh</button>
      </div>

      {flash && <div style={{ padding: "10px 14px", background: "#DCFCE7", color: "#15803D",
        border: "1px solid #86EFAC", borderRadius: 10, fontSize: 12.5, marginBottom: 14 }}>{flash}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
        <div>
          <Card title="Roles">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
              {ROLE_OPTIONS.map((r) => (
                <label key={r} data-testid={`role-${r}`} style={{
                  display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 10px",
                  borderRadius: 999, border: `1px solid ${roles.includes(r) ? "#22C55E" : ig.border}`,
                  background: roles.includes(r) ? "#DCFCE7" : ig.card,
                  color: roles.includes(r) ? "#15803D" : ig.ink, fontSize: 12, fontWeight: 700, cursor: "pointer",
                }}>
                  <input type="checkbox" checked={roles.includes(r)} onChange={() => toggleRole(r)} style={{ accentColor: "#22C55E" }} />
                  {r}
                </label>
              ))}
            </div>
            <button onClick={saveRoles} style={btnPrimary} data-testid="ud-save-roles" disabled={busy}>Save roles</button>
          </Card>

          <Card title={`Workspaces (${d.workspaces?.length || 0})`}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
              <thead><tr style={{ background: "#FAFCFB" }}><Th>Workspace</Th><Th>Plan</Th><Th>Balance</Th><Th>Actions</Th></tr></thead>
              <tbody>
                {(d.workspaces || []).map((w) => {
                  const b = (d.balances || []).find((x) => x.workspace_id === w.id);
                  return (
                    <tr key={w.id} style={{ borderTop: `1px solid ${ig.borderSoft}` }}>
                      <Td>
                        <Link to={`/admin/workspaces/${w.id}/contacts`} style={linkStyle} data-testid={`open-ws-${w.id}`}>
                          {w.name} <ExternalLink size={11} />
                        </Link>
                      </Td>
                      <Td>{w.plan || "—"}</Td>
                      <Td>{b ? b.balance : 0}</Td>
                      <Td><button style={btnMini} onClick={() => adjustCredits(w)} data-testid={`adjust-credits-${w.id}`}><Coins size={11} /> Adjust</button></Td>
                    </tr>
                  );
                })}
                {(d.workspaces || []).length === 0 && <tr><td colSpan={4} style={{ padding: 14, color: ig.muted, fontSize: 12 }}>Not a member of any workspace.</td></tr>}
              </tbody>
            </table>
          </Card>

          <Card title="Activity summary">
            <RailRow label="Owned contacts">{d.contact_count}</RailRow>
            <RailRow label="Contact activities logged">{d.activity_count}</RailRow>
            <RailRow label="Agency">{d.agency ? <Link to={`/admin/agencies/${d.agency.id}/contacts`} style={linkStyle} data-testid="open-agency-from-user">{d.agency.name} · {d.agency.tier} <ExternalLink size={11} /></Link> : "—"}</RailRow>
          </Card>
        </div>

        <div>
          <Card title="Admin actions">
            <ActionBtn Icon={LogIn}       label="Impersonate user"      onClick={impersonate} testId="ud-impersonate" />
            <ActionBtn Icon={Key}         label="Reset access"          onClick={reset}       testId="ud-reset" />
            {u.status === "disabled"
              ? <ActionBtn Icon={ShieldCheck} label="Enable login"       onClick={enable}      testId="ud-enable" tone="green" />
              : <ActionBtn Icon={ShieldOff}   label="Disable login"      onClick={disable}     testId="ud-disable" tone="red" />}
            <Link to={`/admin/contacts?owner_user_id=${u.id}`} style={{ ...actionRow, textDecoration: "none" }} data-testid="ud-view-contacts">
              <UsersIcon size={13} /> View this user&apos;s contacts →
            </Link>
          </Card>
          <Card title="Profile">
            <RailRow icon={User}  label="Name">{u.name}</RailRow>
            <RailRow icon={Mail}  label="Email">{u.email}</RailRow>
            <RailRow            label="ID">{u.id}</RailRow>
            <RailRow            label="Created">{fmt(u.created_at)}</RailRow>
            <RailRow            label="Updated">{fmt(u.updated_at)}</RailRow>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Card({ title, children }) { return <div style={{ background: ig.card, border: `1px solid ${ig.border}`, borderRadius: 12, padding: 16, marginBottom: 14 }}><div style={{ fontSize: 12, fontWeight: 800, color: ig.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 12 }}>{title}</div>{children}</div>; }
function Th({ children }) { return <th style={{ textAlign: "left", padding: "9px 10px", fontSize: 11, fontWeight: 700, color: ig.muted }}>{children}</th>; }
function Td({ children }) { return <td style={{ padding: "9px 10px", fontSize: 12.5, color: ig.ink }}>{children}</td>; }
function RailRow({ icon: Icon, label, children }) { return <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: `1px dashed ${ig.borderSoft}`, fontSize: 12.5 }}>{Icon && <Icon size={12} color={ig.muted} />}<div style={{ color: ig.muted, minWidth: 90 }}>{label}</div><div style={{ flex: 1, color: ig.ink, fontWeight: 600 }}>{children}</div></div>; }
function ActionBtn({ Icon, label, onClick, testId, tone }) {
  const c = tone === "red" ? { color: "#B91C1C", border: "#FECACA" } : tone === "green" ? { color: "#15803D", border: "#86EFAC" } : { color: ig.ink, border: ig.border };
  return <button onClick={onClick} data-testid={testId} style={{ ...actionRow, color: c.color, borderColor: c.border }}><Icon size={13} /> {label}</button>;
}
function StatusPill({ status }) {
  const map = { active: { bg: "#DCFCE7", fg: "#15803D" }, disabled: { bg: "#FECACA", fg: "#991B1B" } };
  const p = map[status] || { bg: "#F1F5F4", fg: ig.muted };
  return <span data-testid="ud-status" style={{ background: p.bg, color: p.fg, padding: "4px 10px", borderRadius: 999, fontSize: 11.5, fontWeight: 700 }}>{status}</span>;
}
function initials(s) { return (s || "?").split(/[ .@]/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join(""); }
function fmt(iso) { if (!iso) return "—"; try { return new Date(iso).toLocaleString(); } catch { return iso; } }

const backBtn    = { display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 10px", borderRadius: 7, border: `1px solid ${ig.border}`, background: ig.card, fontSize: 12, color: ig.ink, cursor: "pointer", fontWeight: 700 };
const btnPrimary = { display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: 0, background: "linear-gradient(135deg, #18B86B, #22C55E)", color: "#fff", fontWeight: 700, fontSize: 12.5, cursor: "pointer" };
const btnGhost   = { display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 8, border: `1px solid ${ig.border}`, background: ig.card, color: ig.ink, fontWeight: 600, fontSize: 12.5, cursor: "pointer" };
const btnMini    = { display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 7, border: `1px solid ${ig.border}`, background: ig.card, color: ig.ink, fontWeight: 700, fontSize: 11.5, cursor: "pointer" };
const actionRow  = { display: "inline-flex", alignItems: "center", gap: 8, width: "100%", padding: "9px 12px", borderRadius: 8, border: `1px solid ${ig.border}`, background: ig.card, cursor: "pointer", fontWeight: 700, fontSize: 12.5, marginBottom: 8, textAlign: "left" };
const linkStyle  = { color: "#2563EB", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 700 };
